import React from 'react';
import { arrayOf, string } from 'prop-types';

import { propTypes } from '../../../util/types';
import { userDisplayNameAsString } from '../../../util/data';
import { AvatarSmall } from '../../../components';

import SelectSingleFilter from '../SelectSingleFilter/SelectSingleFilter';

import css from './SelectAuthorFilter.module.css';

/**
 * Component that wraps the author's avatar and display name.
 *
 * - This **only formats** the avatar and text for easier reuse.
 * - It does **not** handle selection, clicks, or form behavior.
 * - This will be rendered **inside an actual `<label>` element** when used in filtering components.
 */
const AuthorOptionLabel = ({ user }) => {
  const displayName = userDisplayNameAsString(user);
  return (
    <span className={css.optionLabel}>
      <AvatarSmall className={css.avatar} user={user} />
      {displayName}
    </span>
  );
};

/**
 * Component that generates filter options for selecting a author.
 *
 * - Maps over the list of users to create filter options.
 * - Each option includes a author's avatar and display name.
 * - Used inside filtering components to display author-specific filtering.
 */
const SelectAuthorFilterComponent = props => {
  const { users, ...filterProps } = props;

  if (users.length === 0) {
    return null;
  }

  const options = users.map(user => ({
    option: user.id.uuid,
    label: <AuthorOptionLabel key={user.id.uuid} user={user} />,
  }));

  return <SelectSingleFilter options={options} {...filterProps} />;
};

SelectAuthorFilterComponent.propTypes = {
  users: arrayOf(propTypes.user).isRequired,
};

// SelectAuthorFilter component is a wrapper around SelectAuthorFilterComponent
const SelectAuthorFilter = props => {
  const { includeFor, currentPage, ...rest } = props;

  if (!includeFor.includes(currentPage)) {
    return null;
  }

  return <SelectAuthorFilterComponent {...rest} />;
};

SelectAuthorFilter.defaultProps = {
  includeFor: [],
};

SelectAuthorFilter.propTypes = {
  includeFor: arrayOf(string),
  currentPage: string.isRequired,
};

export default SelectAuthorFilter;
